import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calendar, Plus, Clock, CheckCircle, XCircle, AlertCircle,
  FileText, Download, ChevronLeft, ChevronRight, Palmtree, Stethoscope, Upload,
  AlertTriangle, User
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const WEEKDAYS = ['Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm', 'Dum'];

const CalendarView = ({ year, month, onMonthChange }) => {
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCalendar();
  }, [year, month]);

  const loadCalendar = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/calendar/${year}/${month}`);
      setCalendarData(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea calendarului');
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    if (month === 1) {
      onMonthChange(year - 1, 12);
    } else {
      onMonthChange(year, month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 12) {
      onMonthChange(year + 1, 1);
    } else {
      onMonthChange(year, month + 1);
    }
  };

  if (loading || !calendarData) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </CardContent>
      </Card>
    );
  }

  // Get first day of month offset
  // Python weekday: 0=Monday, 6=Sunday
  // Calendar starts with Monday, so padding = weekday directly
  const firstDayOffset = calendarData.days[0]?.weekday || 0;
  const paddingDays = firstDayOffset;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={prevMonth}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <CardTitle className="text-lg">
            {calendarData.month_name} {calendarData.year}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={nextMonth}>
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4 text-center text-xs">
          <div className="bg-slate-50 rounded p-2">
            <p className="font-bold text-lg">{calendarData.total_working_days}</p>
            <p className="text-muted-foreground">Zile lucr.</p>
          </div>
          <div className="bg-emerald-50 rounded p-2">
            <p className="font-bold text-lg text-emerald-600">{calendarData.total_worked_days}</p>
            <p className="text-muted-foreground">Lucrate</p>
          </div>
          <div className="bg-amber-50 rounded p-2">
            <p className="font-bold text-lg text-amber-600">{calendarData.total_holidays}</p>
            <p className="text-muted-foreground">Sărbători</p>
          </div>
          <div className="bg-blue-50 rounded p-2">
            <p className="font-bold text-lg text-blue-600">{calendarData.total_leave_days}</p>
            <p className="text-muted-foreground">Concediu</p>
          </div>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((day, i) => (
            <div 
              key={day} 
              className={`text-center text-xs font-medium py-1 ${i >= 5 ? 'text-red-400' : 'text-muted-foreground'}`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {/* Padding for first week */}
          {Array.from({ length: paddingDays }).map((_, i) => (
            <div key={`pad-${i}`} className="h-10"></div>
          ))}
          
          {calendarData.days.map((day) => {
            let bgColor = 'bg-white hover:bg-slate-50';
            let textColor = 'text-slate-700';
            let title = '';
            
            if (day.is_weekend) {
              bgColor = 'bg-slate-100';
              textColor = 'text-slate-400';
              title = 'Weekend';
            }
            if (day.is_holiday) {
              bgColor = 'bg-amber-100';
              textColor = 'text-amber-700';
              title = day.holiday_name || 'Sărbătoare';
            }
            if (day.is_leave) {
              bgColor = 'bg-blue-100';
              textColor = 'text-blue-700';
              title = 'Concediu';
            }
            if (day.is_worked) {
              bgColor = 'bg-emerald-100';
              textColor = 'text-emerald-700';
              title = 'Zi lucrată';
            }

            return (
              <div
                key={day.date}
                className={`h-10 flex items-center justify-center rounded text-sm font-medium ${bgColor} ${textColor} cursor-default transition-colors`}
                title={title}
              >
                {day.day}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-emerald-100"></div>
            <span>Lucrat</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-100"></div>
            <span>Concediu</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-100"></div>
            <span>Sărbătoare</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-slate-100"></div>
            <span>Weekend</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const LeaveRequestDialog = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    leave_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [medicalCertificate, setMedicalCertificate] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMedicalCertificate(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Create leave request
      const response = await api.post('/leave-requests', formData);
      
      // If medical leave and certificate provided, upload it
      if (formData.leave_type === 'medical' && medicalCertificate) {
        const uploadData = new FormData();
        uploadData.append('file', medicalCertificate);
        uploadData.append('leave_request_id', response.data.id);
        
        try {
          await api.post('/leave-requests/upload-certificate', uploadData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadErr) {
          console.log('Certificate upload skipped:', uploadErr);
        }
      }
      
      toast.success('Cererea a fost trimisă pentru aprobare!');
      setFormData({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
      setMedicalCertificate(null);
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la trimiterea cererii');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palmtree className="w-5 h-5 text-emerald-500" />
            Cerere Concediu
          </DialogTitle>
          <DialogDescription>
            Completează detaliile și trimite cererea pentru aprobare
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Tip Concediu *</Label>
            <Select 
              value={formData.leave_type} 
              onValueChange={(v) => {
                setFormData({ ...formData, leave_type: v });
                if (v !== 'medical') setMedicalCertificate(null);
              }}
            >
              <SelectTrigger data-testid="leave-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vacation">
                  <div className="flex items-center gap-2">
                    <Palmtree className="w-4 h-4 text-emerald-500" />
                    Concediu de Odihnă
                  </div>
                </SelectItem>
                <SelectItem value="medical">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-blue-500" />
                    Concediu Medical
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Data Început *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
                data-testid="leave-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">Data Sfârșit *</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
                data-testid="leave-end-date"
              />
            </div>
          </div>

          {formData.leave_type === 'medical' && (
            <div className="space-y-2">
              <Label htmlFor="certificate">Certificat Medical (opțional)</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  id="certificate"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="medical-certificate-input"
                />
                <label 
                  htmlFor="certificate" 
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  {medicalCertificate ? (
                    <span className="text-sm text-emerald-600 font-medium">
                      {medicalCertificate.name}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      Click pentru a încărca certificatul medical
                    </span>
                  )}
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Acceptă: PDF, JPG, PNG (max 5MB)
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">Motiv (opțional)</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder={formData.leave_type === 'medical' ? "ex: Probleme de sănătate..." : "ex: Vacanță de familie..."}
              rows={2}
              data-testid="leave-reason"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-leave-btn"
            >
              {saving ? 'Se trimite...' : 'Trimite Cererea'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LeaveRequestCard = ({ request, isAdmin, onApprove, onReject, onDownload }) => {
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700'
  };

  const statusLabels = {
    pending: 'În așteptare',
    approved: 'Aprobat',
    rejected: 'Respins'
  };

  const typeIcons = {
    vacation: <Palmtree className="w-4 h-4 text-emerald-500" />,
    medical: <Stethoscope className="w-4 h-4 text-blue-500" />
  };

  const typeLabels = {
    vacation: 'Concediu Odihnă',
    medical: 'Concediu Medical'
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {typeIcons[request.leave_type]}
            <span className="font-medium">{typeLabels[request.leave_type]}</span>
          </div>
          <Badge className={statusColors[request.status]}>
            {statusLabels[request.status]}
          </Badge>
        </div>

        {isAdmin && (
          <p className="text-sm font-medium mb-2">{request.user_name}</p>
        )}

        <div className="text-sm text-muted-foreground space-y-1">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>
              {new Date(request.start_date).toLocaleDateString('ro-RO')} - {new Date(request.end_date).toLocaleDateString('ro-RO')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{request.num_days} {request.num_days === 1 ? 'zi lucrătoare' : 'zile lucrătoare'}</span>
          </div>
        </div>

        {request.reason && (
          <p className="text-sm mt-2 text-muted-foreground italic">"{request.reason}"</p>
        )}

        {request.rejection_reason && (
          <p className="text-sm mt-2 text-red-600">Motiv respingere: {request.rejection_reason}</p>
        )}

        <div className="flex gap-2 mt-4">
          {isAdmin && request.status === 'pending' && (
            <>
              <Button 
                size="sm" 
                className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                onClick={() => onApprove(request.id)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Aprobă
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                className="flex-1"
                onClick={() => onReject(request.id)}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Respinge
              </Button>
            </>
          )}
          {request.status === 'approved' && (
            <Button 
              size="sm" 
              variant="outline"
              className="w-full"
              onClick={() => onDownload(request.id)}
            >
              <Download className="w-4 h-4 mr-1" />
              Descarcă PDF
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const LeaveManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [profileComplete, setProfileComplete] = useState(true);
  const [missingFields, setMissingFields] = useState([]);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const checkProfileComplete = (profile) => {
    const requiredFields = [
      { key: 'cnp', label: 'CNP' },
      { key: 'ci_series', label: 'Seria CI' },
      { key: 'ci_number', label: 'Număr CI' },
      { key: 'position', label: 'Funcție' }
    ];
    
    const missing = requiredFields.filter(f => !profile[f.key] || profile[f.key].trim() === '');
    setMissingFields(missing.map(f => f.label));
    setProfileComplete(missing.length === 0);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsRes, balanceRes, profileRes] = await Promise.all([
        api.get('/leave-requests'),
        api.get('/leave-balance'),
        api.get(`/users/${user.id}/employee-profile`)
      ]);
      setRequests(requestsRes.data);
      setLeaveBalance(balanceRes.data);
      checkProfileComplete(profileRes.data);
    } catch (err) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await api.put(`/leave-requests/${requestId}/approve`);
      toast.success('Cererea a fost aprobată!');
      loadData();
    } catch (err) {
      toast.error('Eroare la aprobarea cererii');
    }
  };

  const handleReject = async (requestId) => {
    const reason = window.prompt('Motiv respingere (opțional):');
    try {
      await api.put(`/leave-requests/${requestId}/reject?rejection_reason=${encodeURIComponent(reason || '')}`);
      toast.success('Cererea a fost respinsă');
      loadData();
    } catch (err) {
      toast.error('Eroare la respingerea cererii');
    }
  };

  const handleDownload = async (requestId) => {
    try {
      // Use direct axios call with responseType blob to ensure proper handling
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leave-requests/${requestId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob and download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cerere_concediu_${requestId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descărcat cu succes!');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Eroare la descărcarea PDF');
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const approvedRequests = requests.filter(r => r.status === 'approved');
  const rejectedRequests = requests.filter(r => r.status === 'rejected');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Profile Incomplete Warning Banner */}
        {!profileComplete && (
          <Card className="bg-amber-50 border-amber-300">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">Profil Incomplet</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Pentru a genera cereri de concediu oficiale, te rugăm să completezi datele tale de identificare.
                    Lipsesc: <span className="font-medium">{missingFields.join(', ')}</span>
                  </p>
                  <Link to="/profile">
                    <Button 
                      size="sm" 
                      className="mt-3 bg-amber-600 hover:bg-amber-700"
                      data-testid="complete-profile-btn"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Completează Profilul
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Palmtree className="w-7 h-7 text-emerald-500" />
              Concedii
            </h1>
            <p className="text-muted-foreground">
              {isAdmin ? 'Gestionează cererile de concediu' : 'Cere și vizualizează concediile tale'}
            </p>
          </div>
          <Button 
            onClick={() => {
              if (!profileComplete) {
                toast.warning('Te rugăm să completezi profilul înainte de a crea o cerere de concediu.', {
                  action: {
                    label: 'Completează Profil',
                    onClick: () => window.location.href = '/profile'
                  }
                });
                return;
              }
              setShowRequestDialog(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="new-leave-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Cerere Nouă
          </Button>
        </div>

        {/* Balance Card */}
        {leaveBalance && (
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Zile Concediu Disponibile</p>
                  <p className="text-3xl font-bold text-emerald-700">
                    {leaveBalance.remaining_days} <span className="text-lg font-normal">zile</span>
                  </p>
                </div>
                <div className="text-right text-sm">
                  <p>Total: {leaveBalance.total_available} zile</p>
                  <p>Folosite: {leaveBalance.used_days} zile</p>
                  {leaveBalance.extra_days > 0 && (
                    <p className="text-emerald-600">+{leaveBalance.extra_days} extra</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-1">
            <CalendarView 
              year={calendarYear} 
              month={calendarMonth}
              onMonthChange={(y, m) => {
                setCalendarYear(y);
                setCalendarMonth(m);
              }}
            />
          </div>

          {/* Requests */}
          <div className="lg:col-span-2">
            <Tabs defaultValue={isAdmin ? "pending" : "all"}>
              <TabsList className="mb-4">
                {isAdmin && (
                  <TabsTrigger value="pending" className="relative">
                    În Așteptare
                    {pendingRequests.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                        {pendingRequests.length}
                      </span>
                    )}
                  </TabsTrigger>
                )}
                <TabsTrigger value="all">Toate</TabsTrigger>
                <TabsTrigger value="approved">Aprobate</TabsTrigger>
                <TabsTrigger value="rejected">Respinse</TabsTrigger>
              </TabsList>

              {isAdmin && (
                <TabsContent value="pending">
                  {pendingRequests.length === 0 ? (
                    <Card>
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Nu există cereri în așteptare</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-4">
                      {pendingRequests.map(req => (
                        <LeaveRequestCard
                          key={req.id}
                          request={req}
                          isAdmin={isAdmin}
                          onApprove={handleApprove}
                          onReject={handleReject}
                          onDownload={handleDownload}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              )}

              <TabsContent value="all">
                {requests.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nu există cereri de concediu</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {requests.map(req => (
                      <LeaveRequestCard
                        key={req.id}
                        request={req}
                        isAdmin={isAdmin}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onDownload={handleDownload}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="approved">
                {approvedRequests.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <p>Nu există cereri aprobate</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {approvedRequests.map(req => (
                      <LeaveRequestCard
                        key={req.id}
                        request={req}
                        isAdmin={isAdmin}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onDownload={handleDownload}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="rejected">
                {rejectedRequests.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <p>Nu există cereri respinse</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {rejectedRequests.map(req => (
                      <LeaveRequestCard
                        key={req.id}
                        request={req}
                        isAdmin={isAdmin}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onDownload={handleDownload}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Request Dialog */}
        <LeaveRequestDialog
          isOpen={showRequestDialog}
          onClose={() => setShowRequestDialog(false)}
          onSave={loadData}
        />
      </div>
    </Layout>
  );
};

export default LeaveManagement;
